import { useEffect, useState } from "react";
import { api } from "@/lib/api";

interface CronJob {
  id: string;
  name: string;
  schedule: any;
  enabled: boolean;
  lastRun?: string;
  nextRun?: string;
}

export function CronPage() {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await api.getCronJobs();
      setJobs(data?.jobs || data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleRun = async (jobId: string) => {
    try {
      await api.runCronJob(jobId);
      fetchJobs(); // Refresh
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <h1 className="text-lg font-semibold text-zinc-100">Cron Jobs</h1>
        <button
          onClick={fetchJobs}
          className="rounded-lg bg-zinc-800 px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-700"
        >
          ↻ Refresh
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-zinc-500">
            Loading...
          </div>
        ) : error ? (
          <div className="rounded-lg bg-red-500/10 p-4 text-sm text-red-400">{error}</div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-zinc-500">
            <div className="text-4xl">⏰</div>
            <p className="text-sm">No cron jobs</p>
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      job.enabled ? "bg-green-500" : "bg-zinc-600"
                    }`}
                  />
                  <div>
                    <div className="text-sm font-medium text-zinc-200">{job.name}</div>
                    <div className="text-xs text-zinc-500">
                      {job.schedule?.kind === "cron"
                        ? job.schedule.expr
                        : job.schedule?.kind === "every"
                        ? `Every ${(job.schedule.everyMs / 1000).toFixed(0)}s`
                        : job.schedule?.at || "—"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRun(job.id)}
                  className="rounded-lg bg-blue-600/10 px-3 py-1.5 text-xs text-blue-400 hover:bg-blue-600/20"
                >
                  ▶ Run
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
